import { Slice } from '@tiptap/pm/model';

const PARAGRAPH_NODE = 'paragraph';

// ProseMirror stamps this on the first element of anything copied out of the editor.
const SLICE_ATTRIBUTE = 'data-pm-slice';

const FRAGMENT_START_MARKER = '<!--StartFragment-->';
const FRAGMENT_END_MARKER = '<!--EndFragment-->';

const DOCUMENT_WRAPPER_TAGS = /<\/?(?:html|head|body)\b[^>]*>/gi;

// Only these five characters collapse when HTML renders, so only they can be cosmetic.
const SURROUNDING_WHITESPACE = /^[ \t\r\n\f]+|[ \t\r\n\f]+$/g;

export class RichTextClipboardUtilities {

    // DEV-2786: Windows hands clipboard HTML back in the OS CF_HTML format, which wraps the copied markup in
    // <html>/<body> and newlines. ProseMirror parses a paste with preserveWhitespace ON whenever the payload
    // carries its own data-pm-slice marker, trusting its own serialisation not to contain cosmetic
    // whitespace — so the wrapper's newlines survive as real hard breaks. Unwrap our own payloads back to
    // what the serialiser emitted. Everything else is left alone: ProseMirror collapses whitespace in HTML it
    // did not write, and second-guessing that is how a paste loses formatting or a non-breaking space.
    public static normalizePastedHtml(html: string): string {
        if (!html.includes(SLICE_ATTRIBUTE)) {
            return html;
        }

        const start = html.indexOf(FRAGMENT_START_MARKER);
        const end = html.lastIndexOf(FRAGMENT_END_MARKER);

        if (start !== -1 && end > start) {
            return html.slice(start + FRAGMENT_START_MARKER.length, end);
        }

        return html.replace(DOCUMENT_WRAPPER_TAGS, '').replace(SURROUNDING_WHITESPACE, '');
    }

    // DEV-2786: Ctrl+A produces an AllSelection, whose slice is a closed block — so pasting it starts a new
    // paragraph instead of continuing the one the caret sits in, which reads as an inserted blank line. A
    // dragged selection is open at both ends and merges inline, so reopen any end that is a plain paragraph
    // and a whole-document copy pastes identically. Headings, lists and rules stay closed so a copy that was
    // genuinely block-level keeps its structure.
    public static openPastedBlockSlice(slice: Slice): Slice {
        const openStart = slice.openStart === 0 && slice.content.firstChild?.type.name === PARAGRAPH_NODE
            ? 1
            : slice.openStart;
        const openEnd = slice.openEnd === 0 && slice.content.lastChild?.type.name === PARAGRAPH_NODE
            ? 1
            : slice.openEnd;

        if (openStart === slice.openStart && openEnd === slice.openEnd) {
            return slice;
        }

        return new Slice(slice.content, openStart, openEnd);
    }
}
